import React, { useState, useEffect, useCallback } from "react";
import { IconButton, Collapse, Typography, Box, Stack } from "@mui/material";
import { ExpandLess, ExpandMore, Star } from "@mui/icons-material";
import { formatWeightWithUnits, UomConversion } from "../../../utils";

const RecipeTable = ({ recipe, globalData }) => {
  const [openRows, setOpenRows] = useState({});

  // Reset state when switching recipes
  useEffect(() => {
    setOpenRows({}); // Clear expanded rows on recipe change
  }, [recipe]);

  const toggleRow = (key) => {
    setOpenRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Memoized function to prevent unnecessary re-renders
  const renderIngredients = useCallback(
    (ingredients = [], level = 0, parentKey = "") => {
      return ingredients.map((ingredient, index) => {
        const isRecipe = ingredient?.type === "recipe";
        const isMain = ingredient?.pivot?.is_main_ingredient === 1;
        const hasChildren =
          isRecipe &&
          Array.isArray(ingredient?.ingredient) &&
          ingredient.ingredient.length > 0;

        const rowKey = `${parentKey}-${ingredient?.id || index}-${level}`;
        const open = openRows[rowKey] || false;

        const quantityText = UomConversion(ingredient);
        const primaryLevel = level === 0;

        // const innerQuantityText = UomConversion(ingredient);

        return (
          <React.Fragment key={rowKey}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "divider",
                px: "8px",
                height: "40px",
                ml: primaryLevel ? 0 : `${level * 10}px`,
              }}
            >
              <Typography
                fontWeight={primaryLevel ? 500 : 400}
                fontSize={"14px"}
              >
                {ingredient?.name ||
                  ingredient?.ingredient_name ||
                  ingredient?.recipe_name}
              </Typography>

              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={() => toggleRow(rowKey)}
                  sx={{ height: "8px", width: "8px", marginLeft: 1 }}
                >
                  {open ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}

              {isMain && (
                <Star
                  fontSize="small"
                  sx={{
                    color: "#1760D2",
                    marginLeft: 1,
                    height: "16px",
                    width: "16px",
                  }}
                />
              )}

              <Typography
                sx={{ marginLeft: "auto", textAlign: "left" }}
                fontWeight={600}
                fontSize="14px"
                color="#485364"
              >
                {quantityText}
              </Typography>
            </Box>

            {hasChildren && (
              <Collapse in={open} timeout="auto" unmountOnExit>
                <div>
                  {renderIngredients(ingredient?.ingredient, level + 1, rowKey)}
                </div>
              </Collapse>
            )}

            {/* {open && innerQuantityText && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  paddingRight: "10px",
                  fontSize: "14px",
                  color: "#555",
                }}
              >
                {innerQuantityText}
              </Box>
            )} */}
          </React.Fragment>
        );
      });
    },
    [openRows, globalData]
  );

  return (
    <Box>
      <Stack
        height="51px"
        justifyContent="center"
        position="sticky"
        top="0"
        backgroundColor="background.paper"
        zIndex={1}
      >
        <Typography fontWeight={600} fontSize="18px">
          {recipe?.recipe_name}
        </Typography>
      </Stack>
      <Box
        border={1}
        borderColor="divider"
        borderRadius="4px"
        overflow="hidden"
      >
        {/* Table Header */}
        <Stack
          sx={{
            background: "#F2F7FC",
            padding: "8px",
          }}
          display="flex"
          justifyContent="space-between"
          direction="row"
        >
          <Typography fontWeight={600} fontSize="12px">
            Ingredients
          </Typography>
          <Typography fontWeight={600} fontSize="12px">
            Quantity
          </Typography>
        </Stack>

        {/* Table Content */}
        <Box key={recipe?.id}>{renderIngredients(recipe?.ingredients)}</Box>

        <Box
          sx={{
            display: "flex",
            padding: "8px",
            minHeight: "40px",
            backgroundColor: "#E6EEF7",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={600} fontSize="14px">
            Total Weight
          </Typography>
          <Typography
            fontWeight={600}
            fontSize="14px"
            minWidth="120px"
            textAlign="right"
          >
            {formatWeightWithUnits(
              recipe?.total_weight,
              "kg",
              globalData.all_Units
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RecipeTable;
